import {
  bookingAssignDriverSchema,
  type BookingAssignDriverPayload,
  type BookingDetail,
  type ShippingWindow,
  type User,
  type Vehicle,
} from '@dc-trans/data-layer';
import { useNotification } from '@/context/notificationContext';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import { FormBuilder } from '@/components/ui/form/FormBuilder';
import { Flex } from 'antd';
import { Button } from 'pink-lava-ui';
import { usePostBookingDriverAssignment } from '@/mutations';
import queryClient from '@/libs/queryClient';
import { useBookingShipmentGroupedV2 } from '@/queries';

const AssignDriverForm = ({ booking }: { booking: BookingDetail }) => {
  const isReassign = !!booking.assignment?.driver?.ulid;
  const showNotification = useNotification();
  const methods = useForm({
    resolver: zodResolver(bookingAssignDriverSchema(isReassign)),
    defaultValues: {
      driver_ulid: booking.assignment?.driver?.ulid,
      vehicle_ulid: booking.assignment?.vendor_vehicle?.ulid,
      delivery_date: booking.assignment?.delivery_date
        ? dayjs(booking.assignment?.delivery_date).format('YYYY-MM-DD')
        : '',
      shipping_window_ulid: booking.assignment.ship_window?.ulid,
      notes: '',
    },
  });

  const [editMode, setEditMode] = useState(!isReassign);

  const { mutate: bookingDriverAssignmentMutate, isPending } = usePostBookingDriverAssignment({
    meta: {
      message: isReassign ? 'Gagal mengubah penugasan driver.' : 'Gagal menugaskan driver.',
    },
  });

  const bookingShipmentGroupedQueryV2 = useBookingShipmentGroupedV2(booking.ulid!, {
    page: 1,
    limit: 100,
  });

  const bookingShipmentGroupedData = bookingShipmentGroupedQueryV2.data?.data?.items || [];

  const isArrivalClaim = bookingShipmentGroupedData.some(bookingShipment => bookingShipment.status === 'arrival_claim');

  const handleFormSubmit = (data: any) => {
    const payload = data as Omit<BookingAssignDriverPayload, 'id'>;
    bookingDriverAssignmentMutate(
      {
        id: booking.ulid!,
        payload: {
          ...payload,
          delivery_date: payload.delivery_date ? dayjs(payload.delivery_date).format('YYYY-MM-DD') : '',
        },
      },
      {
        onSuccess: () => {
          showNotification.success({
            message: isReassign ? 'Penugasan berhasil diubah' : 'Driver berhasil ditugaskan',
          });
          setEditMode(false);
          methods.setValue('notes', '');
          queryClient.invalidateQueries({ queryKey: ['booking'] });
        },
      }
    );
  };

  const onFinishEdit = () => {
    setEditMode(false);
    methods.reset({
      vehicle_ulid: booking.assignment?.vendor_vehicle?.ulid,
      delivery_date: booking.assignment?.delivery_date
        ? dayjs(booking.assignment?.delivery_date).format('YYYY-MM-DD')
        : '',
      shipping_window_ulid: booking.assignment.ship_window?.ulid,
      notes: '',
    });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleFormSubmit)} className="space-y-2">
        <FormBuilder
          methods={methods}
          isResponsive
          colProps={{ xs: 24, lg: 12, xxl: 6 }}
          disabled={!editMode}
          fields={[
            {
              variant: !editMode ? 'filled' : undefined,
              name: 'driver_ulid',
              type: 'select',
              label: 'Pengemudi',
              apiUrl: 'drivers',
              searchKey: 'search',
              apiParams: {
                page: 1,
                limit: 100,
                status: 'active',
              },
              placeholder: 'Pilih Pengemudi',
              renderOption: item => {
                const driver = item as User;
                return {
                  label: `${driver.name} - ${driver.username}`,
                  value: driver.ulid!,
                };
              },
            },
            {
              variant: !editMode ? 'filled' : undefined,
              name: 'vehicle_ulid',
              label: 'Kendaraan',
              type: 'select',
              apiUrl: 'vehicles',
              searchKey: 'search',
              apiParams: {
                page: 1,
                limit: 100,
                status: 'active',
              },
              placeholder: 'Pilih Nomor & Tipe Kendaraan',
              renderOption: item => {
                const vehicle = item as Vehicle;
                return {
                  label: `${vehicle.license_plate} - ${vehicle.vehicle_type?.name}`,
                  value: vehicle.ulid!,
                };
              },
            },
            {
              variant: !editMode ? 'filled' : undefined,
              name: 'delivery_date',
              label: 'Tanggal Pengambilan Barang',
              type: 'date',
              onChange: (value: Dayjs, setValue) => {
                setValue(value ? dayjs(value).format('YYYY-MM-DD') : '');
              },
              placeholder: 'Pilih Tanggal',
            },
            {
              variant: !editMode ? 'filled' : undefined,
              name: 'shipping_window_ulid',
              type: 'select',
              label: 'Shipping Window',
              placeholder: 'Shipping Window',
              searchKey: 'name',
              apiUrl: 'shipping-windows',
              apiParams: {
                page: 1,
                limit: 100,
              },
              renderOption: item => {
                const shippingWindow = item as ShippingWindow;
                return {
                  label: `${shippingWindow.name} | ${shippingWindow.incoming} - ${shippingWindow.outgoing}`,
                  value: shippingWindow.ulid,
                };
              },
            },
            {
              name: 'notes',
              placeholder: 'Tulis Alasan',
              type: 'text',
              colProps: {
                xs: 24,
              },
              hidden: !isReassign || !editMode,
            },
          ]}
        />
        <Flex justify="end">
          {booking.status !== 'completed' ? (
            isReassign && !editMode ? (
              <>
                <Button
                  htmlType="button"
                  type="secondary"
                  onClick={e => {
                    e.preventDefault();
                    setEditMode(true);
                  }}
                  disabled={isArrivalClaim}
                >
                  Ubah Penugasan
                </Button>
              </>
            ) : (
              <div className="flex flex-row gap-2">
                {isReassign && !isPending && (
                  <Button
                    htmlType="button"
                    type="ghost"
                    onClick={e => {
                      e.preventDefault();
                      onFinishEdit();
                    }}
                  >
                    Batalkan
                  </Button>
                )}
                <Button
                  type="primary"
                  htmlType="submit"
                  size="small"
                  loading={isPending}
                  disabled={!methods.formState.isDirty || isPending}
                >
                  {isReassign ? 'Ubah Penugasan' : 'Tugaskan Kendaraan'}
                </Button>
              </div>
            )
          ) : null}
        </Flex>
      </form>
    </FormProvider>
  );
};

export default AssignDriverForm;
